/**
 * Return an array of the currently available products.
 * currently this just simulates getting products from some
 * future backend but does currently return real data for
 * use with the Product Cart
 *
 * @param config - product configuration/filter/settings
 * @returns {Promise<void>}
 */
import {
  MM_CALCULATOR,
  MM_CALCULATOR_TYPE, MM_PAYMENT, MM_PAYMENT_EFT, MM_PAYMENT_EFT_ACCOUNT_TYPE, MM_PAYMENT_TYPE,
  MM_PRODUCT, MM_PRODUCT_DTO, MM_PRODUCT_DTO_FIA, MM_PRODUCT_DTO_MYGA, MM_PRODUCT_FAMILY,
  MM_PRODUCT_ID,
  MM_PRODUCT_TYPE,
  MM_PRODUCT_VISUALS
} from "./myga-model";
import {createObjectForKeys, duplicateObject, PARTY, STORAGE_KEYS} from "./constants";
import {isValid, mygaSession} from "./myga-session";
import NassauLogo from "../Resources/Media/nsre-logo.png";
import {PAGE_FLOW} from "./nfg-myga";
import {NassauLog} from "./myga-utils";
import MYGA_STRINGS from "../Resources/Content/MYGAStrings";
import {v4 as uuidv4} from "uuid";

const nfgSAImportantDisclosures = "Future values and interest are gross amounts based on the above indicated single premium with interest credited daily at the current rate for the length of the applicable guarantee period, assuming no withdrawals are made. Local, state and federal taxes may apply. \n" +
    "\n" +
    "If you choose to withdraw money from your annuity, charges and adjustments may eliminate the interest credited to your contract and could result in the loss of principal. Rates are only guaranteed for the duration of the initial guarantee period and are subject to change for future guarantee periods (if available). \n" +
    "\n" +
    "The rate shown above is subject to change at any time. View the Product Disclosure or contact Nassau for rate lock rules and deadlines. \n" +
    "\n" +
    "Nassau Simple Annuity (18FADTCP and ICC18FADTCP) is a single premium deferred fixed annuity issued by Nassau Life and Annuity Company (Hartford, CT) and allows withdrawals of up to 5% annually from the contract value without a surrender charge or market value adjustment. Nassau Life and Annuity Company is not authorized to conduct business in ME and NY, but that is subject to change.  Nassau Life and Annuity Company is a subsidiary of Nassau Financial Group." +
    "\n";

const nfgMAImportantDisclosures = "Future values and interest are gross amounts based on the above indicated single premium with interest credited daily at the current rate for the length of the applicable guarantee period, assuming no withdrawals are made. Local, state and federal taxes may apply. \n" +
    "\n" +
    "If you choose to withdraw money from your annuity, charges and adjustments may eliminate the interest credited to your contract and could result in the loss of principal. Rates are only guaranteed for the duration of the initial guarantee period and are subject to change for future guarantee periods (if available). \n" +
    "\n" +
    "The rate shown above is subject to change at any time. View the Product Disclosure or contact Nassau for rate lock rules and deadlines. \n" +
    "\n" +
    "Nassau MYAnnuity 5X/7X (18IFDAP and ICC18IFDAP/ICC18IFDANP) is a single premium deferred fixed annuity issued by Nassau Life and Annuity Company (Hartford, CT) and allows withdrawals of up to 10% annually from the contract value without a surrender charge or market value adjustment. Nassau Life and Annuity Company is not authorized to conduct business in ME and NY, but that is subject to change.  Nassau Life and Annuity Company is a subsidiary of Nassau Financial Group." +
    "\n";

const nfgMAImportantDisclosuresNoFWA = "Future values and interest are gross amounts based on the above indicated single premium with interest credited daily at the current rate for the length of the applicable guarantee period, assuming no withdrawals are made. Local, state and federal taxes may apply. \n" +
    "\n" +
    "If you choose to withdraw money from your annuity, charges and adjustments may eliminate the interest credited to your contract and could result in the loss of principal. Rates are only guaranteed for the duration of the initial guarantee period and are subject to change for future guarantee periods (if available). \n" +
    "\n" +
    "The rate shown above is subject to change at any time. View the Product Disclosure or contact Nassau for rate lock rules and deadlines. \n" +
    "\n" +
    "Nassau MYAnnuity 5X/7X (18IFDAP and ICC18IFDAP/ICC18IFDANP) is a single premium deferred fixed annuity issued by Nassau Life and Annuity Company (Hartford, CT).  Any withdrawals may be subject to a surrender charge and Market Value Adjustment. Nassau Life and Annuity Company is not authorized to conduct business in ME and NY, but that is subject to change.  Nassau Life and Annuity Company is a subsidiary of Nassau Financial Group." +
    "\n";

export const getAvailableProducts = (config) => {
  // Build up product offerings for NFG Simple Annuity 4 and 6 Year
  let products = [];

  products.push(createProduct(MM_PRODUCT_ID.nfgSA0405));
  products.push(createProduct(MM_PRODUCT_ID.nfgSA0605));
  products.push(createProduct(MM_PRODUCT_ID.nfgMA0500));
  products.push(createProduct(MM_PRODUCT_ID.nfgMA0510));
  products.push(createProduct(MM_PRODUCT_ID.nfgMA0700));
  products.push(createProduct(MM_PRODUCT_ID.nfgMA0710));
  
  // DEMO Products
  // products.push(createProduct(MM_PRODUCT_ID.nfgMA0500));
  // products.push(createProduct(MM_PRODUCT_ID.nfgMA0700));
  
  // NassauLog(JSON.stringify(products,null,2));
  
  return products;
};

export const duplicateProduct = (product) => {
  let customized = duplicateObject(product);
  // mark as customized
  customized[MM_PRODUCT.customized] = true;
  // reapply the calculator since functions don't survive current duplication method
  customized = applyProductCalculator(customized);
  // reapply the visuals since functions don't survive the current duplication method
  customized = applyProductVisuals(customized);
  return customized;
};

export const productForId = (pid) => {
  const products = getAvailableProducts();
  if (products.length===0) {
    return null;
  }
  const index = products.findIndex(p=>(p[MM_PRODUCT.productID]===pid));
  if (index<0) {
    return null;
  }
  return duplicateProduct(products[index]);
}

export const dtoForProduct = (product) => {
  const dto = product[MM_PRODUCT.dto] || createObjectForKeys(MM_PRODUCT_DTO,null);
  dto[MM_PRODUCT_DTO.productID] = product[MM_PRODUCT.productID];
  dto[MM_PRODUCT_DTO.productFamily] = product[MM_PRODUCT.productFamily];
  dto[MM_PRODUCT_DTO.productType] = product[MM_PRODUCT.productType];
  dto[MM_PRODUCT_DTO.premium] = product[MM_PRODUCT.calculator][MM_CALCULATOR.premium];
  dto[MM_PRODUCT_DTO.rate] = product[MM_PRODUCT.calculator][MM_CALCULATOR.rate];
  dto[MM_PRODUCT_DTO.surrenderPeriod] = product[MM_PRODUCT.calculator][MM_CALCULATOR.surrenderPeriod];
  dto[MM_PRODUCT_DTO.freeWithdrawalAmount] = product[MM_PRODUCT.calculator][MM_CALCULATOR.freeWithdrawalAmount];
  if (!dto[MM_PRODUCT_DTO.parties]) dto[MM_PRODUCT_DTO.parties] = [];
  if (!dto[MM_PRODUCT_DTO.benes]) dto[MM_PRODUCT_DTO.benes] = [];
  if (!dto[MM_PRODUCT_DTO.payments]) dto[MM_PRODUCT_DTO.payments] = [];
  return dto;
}

export const productForDTO = (dto) => {
  const product = productForId(dto[MM_PRODUCT_DTO.productID]);
  product[MM_PRODUCT.calculator][MM_CALCULATOR.premium] = dto[MM_PRODUCT_DTO.premium];
  product[MM_PRODUCT.userModified] = (dto!==null);
  product[MM_PRODUCT.dto] = dto;
  return product;
}

const createProduct = (productId) => {
  let product = createProductObject();
  product = applyProductID(product)(productId);
  product = applyProductType(product);
  product = applyProductCalculator(product);
  product = applyProductVisuals(product);
  return product;
};

const createProductObject = () => {
  let product = createObjectForKeys(MM_PRODUCT,null);
  product[MM_PRODUCT.customized] = false;
  product[MM_PRODUCT.userModified] = false;
  return product;
};

const applyProductID = (product) => (productId) => {
  product[MM_PRODUCT.productID] = productId;
  return product;
};

const applyProductType = (product) => {
  switch(product[MM_PRODUCT.productID]) {
    case MM_PRODUCT_ID.nfgSA0405:
    case MM_PRODUCT_ID.nfgSA0605:
      product[MM_PRODUCT.productType] = MM_PRODUCT_TYPE.myga;
      product[MM_PRODUCT.productFamily] = MM_PRODUCT_FAMILY.nfgSA;
      product[MM_PRODUCT.productName] = "Nassau Simple Annuity";
      product[MM_PRODUCT.productBrand] = "Nassau";
      break;
    case MM_PRODUCT_ID.nfgMA0500:
    case MM_PRODUCT_ID.nfgMA0700:
    case MM_PRODUCT_ID.nfgMA0510:
    case MM_PRODUCT_ID.nfgMA0710:
      product[MM_PRODUCT.productType] = MM_PRODUCT_TYPE.myga;
      product[MM_PRODUCT.productFamily] = MM_PRODUCT_FAMILY.nfgMA;
      product[MM_PRODUCT.productName] = "Nassau MYAnnuity";
      product[MM_PRODUCT.productBrand] = "Nassau";
      break;
    default:
      // TODO: unsupported product ID
      break;
  }
  return product;
};

const applyProductCalculator = (product) => {
  switch(product[MM_PRODUCT.productType]) {
    case MM_PRODUCT_TYPE.myga:
      applyMYGACalculator(product);
      break;
    default:
      // TODO: unsupported calculator type
      break;
  }
  return product;
};

const getCurrentRateForProductID = (productID) => {
  let rate = 0.0;
  switch(productID) {
    case MM_PRODUCT_ID.nfgSA0405:
      rate = parseFloat(mygaSession.getProductRates(MM_PRODUCT_FAMILY.nfgSA)['rates'][0]['accounts'][0]['4-Year'].slice(0,-1)) || 0;
      break;
    case MM_PRODUCT_ID.nfgSA0605:
      rate = parseFloat(mygaSession.getProductRates(MM_PRODUCT_FAMILY.nfgSA)['rates'][0]['accounts'][0]['6-Year'].slice(0,-1)) || 0;
      break;
    case MM_PRODUCT_ID.nfgMA0500:
      rate = parseFloat(mygaSession.getProductRates(MM_PRODUCT_FAMILY.nfgMA)['rates'][0]['accounts'][0]['5-Year'].slice(0,-1)) || 0;
      break;
    case MM_PRODUCT_ID.nfgMA0510:
      rate = parseFloat(mygaSession.getProductRates(MM_PRODUCT_FAMILY.nfgMA)['rates'][0]['accounts'][1]['5-Year'].slice(0,-1)) || 0;
      break;
    case MM_PRODUCT_ID.nfgMA0700:
      rate = parseFloat(mygaSession.getProductRates(MM_PRODUCT_FAMILY.nfgMA)['rates'][0]['accounts'][0]['7-Year'].slice(0,-1)) || 0;
      break;
    case MM_PRODUCT_ID.nfgMA0710:
      rate = parseFloat(mygaSession.getProductRates(MM_PRODUCT_FAMILY.nfgMA)['rates'][0]['accounts'][1]['7-Year'].slice(0,-1)) || 0;
      break;
    default:
      break;
  }
  return rate;
}

const applyMYGACalculator = (product) => {
  const calc = product[MM_PRODUCT.calculator] || createObjectForKeys(MM_CALCULATOR,null);
  
  // Set the calculator type in case we need lookup later
  calc[MM_CALCULATOR.calculatorType] = MM_CALCULATOR_TYPE.myga;
  
  // setup the rate
  calc[MM_CALCULATOR.rate] = getCurrentRateForProductID(product[MM_PRODUCT.productID]);
  
  // Setup configurable data based on productID
  // TODO: revisit where we get this info.  Ideally wil come from an API.
  switch(product[MM_PRODUCT.productID]) {
    case MM_PRODUCT_ID.nfgSA0405: {
      calc[MM_CALCULATOR.surrenderPeriod] = 4;
      calc[MM_CALCULATOR.premiumMin] = 10000;
      calc[MM_CALCULATOR.premiumMax] = 2000000;
      calc[MM_CALCULATOR.freeWithdrawalAmount] = 5;
      break;
    }
    case MM_PRODUCT_ID.nfgSA0605: {
      calc[MM_CALCULATOR.surrenderPeriod] = 6;
      calc[MM_CALCULATOR.premiumMin] = 10000;
      calc[MM_CALCULATOR.premiumMax] = 2000000;
      calc[MM_CALCULATOR.freeWithdrawalAmount] = 5;
      break;
    }
    case MM_PRODUCT_ID.nfgMA0500: {
      calc[MM_CALCULATOR.surrenderPeriod] = 5;
      calc[MM_CALCULATOR.premiumMin] = 10000;
      calc[MM_CALCULATOR.premiumMax] = 2000000;
      calc[MM_CALCULATOR.freeWithdrawalAmount] = 0;
      break;
    }
    case MM_PRODUCT_ID.nfgMA0510: {
      calc[MM_CALCULATOR.surrenderPeriod] = 5;
      calc[MM_CALCULATOR.premiumMin] = 10000;
      calc[MM_CALCULATOR.premiumMax] = 2000000;
      calc[MM_CALCULATOR.freeWithdrawalAmount] = 10;
      break;
    }
    case MM_PRODUCT_ID.nfgMA0700: {
      calc[MM_CALCULATOR.surrenderPeriod] = 7;
      calc[MM_CALCULATOR.premiumMin] = 10000;
      calc[MM_CALCULATOR.premiumMax] = 2000000;
      calc[MM_CALCULATOR.freeWithdrawalAmount] = 0;
      break;
    }
    case MM_PRODUCT_ID.nfgMA0710: {
      calc[MM_CALCULATOR.surrenderPeriod] = 7;
      calc[MM_CALCULATOR.premiumMin] = 10000;
      calc[MM_CALCULATOR.premiumMax] = 2000000;
      calc[MM_CALCULATOR.freeWithdrawalAmount] = 10;
      break;
    }
    default:
      break;
  }
  
  if (!calc[MM_CALCULATOR.premium]) {
    calc[MM_CALCULATOR.premium] = calc[MM_CALCULATOR.premiumMin];
  }
  
  // Setup the calculator functions common to all simple annuity products
  const calculateValue = (amount, term) => {
    const rate = calc[MM_CALCULATOR.rate]/100;
    return (amount * Math.pow((1.0 + rate), term));
  };
  const calculateInterest = (amount, term) => (calculateValue(amount,term) - amount);
  calc[MM_CALCULATOR.calculateValue] = calculateValue;
  calc[MM_CALCULATOR.calculateInterest] = calculateInterest;
  
  // apply the calculator to the product
  product[MM_PRODUCT.calculator] = calc;
  
  return product;
};

const applyProductVisuals = (product) => {
  const calc = product[MM_PRODUCT.calculator] || applyMYGACalculator(product)[MM_PRODUCT.calculator];
  const visuals = product[MM_PRODUCT.visuals] || createObjectForKeys(MM_PRODUCT_VISUALS,null);
  visuals[MM_PRODUCT_VISUALS.enabled] = true;
  
  // configure comment elements
  visuals[MM_PRODUCT_VISUALS.logoUrl] = NassauLogo;
  visuals[MM_PRODUCT_VISUALS.themeCss] = "theme-default";
  visuals[MM_PRODUCT_VISUALS.surrenderPeriod] = `${calc[MM_CALCULATOR.surrenderPeriod]}-Year`;
  visuals[MM_PRODUCT_VISUALS.surrenderPeriodLabel] = "Surrender Charge Period";
  // NassauLog(`calc[MM_CALCULATOR.rate] => ${calc[MM_CALCULATOR.rate]}`);
  if (calc[MM_CALCULATOR.freeWithdrawalAmount]>0) {
    visuals[MM_PRODUCT_VISUALS.withdrawalRateInfo] = ()=>{
      return (
          <div>
            {'@'}
            <strong>{`${calc[MM_CALCULATOR.rate].toFixed(2)}%`}</strong>
            {' with a '}
            <strong>{`${calc[MM_CALCULATOR.freeWithdrawalAmount]}% `}</strong>
            Free Annual Withdrawal
          </div>
      )
    }
  } else {
    visuals[MM_PRODUCT_VISUALS.withdrawalRateInfo] = ()=>{
      return (
          <div>
            {'@'}
            <strong>{`${calc[MM_CALCULATOR.rate].toFixed(2)}%`}</strong>
            {' with '}
            <strong>NO</strong> penalty-free withdrawals
          </div>
      )
    }
    // visuals[MM_PRODUCT_VISUALS.withdrawalRateInfo] = `@ ${calc[MM_CALCULATOR.rate].toFixed(2)}% with NO penalty-free withdrawals`;
  }
  
  // configure customized elements
  switch(product[MM_PRODUCT.productID]) {
    case MM_PRODUCT_ID.nfgSA0405: {
      visuals[MM_PRODUCT_VISUALS.cardColor] = "#457abb";
      visuals[MM_PRODUCT_VISUALS.cardColorDefault] = "#457abb";
      visuals[MM_PRODUCT_VISUALS.title] = "Nassau Simple Annuity";
      visuals[MM_PRODUCT_VISUALS.tradeMark] = "";
      visuals[MM_PRODUCT_VISUALS.info] = MYGA_STRINGS.Dashboard_InfoNassauSimpleAnnuity;
      visuals[MM_PRODUCT_VISUALS.importantDisclosures] = nfgSAImportantDisclosures;
      break;
    }
    case MM_PRODUCT_ID.nfgSA0605: {
      visuals[MM_PRODUCT_VISUALS.cardColor] = "#445c83";
      visuals[MM_PRODUCT_VISUALS.cardColorDefault] = "#445c83";
      visuals[MM_PRODUCT_VISUALS.title] = "Nassau Simple Annuity";
      visuals[MM_PRODUCT_VISUALS.tradeMark] = "";
      visuals[MM_PRODUCT_VISUALS.info] = MYGA_STRINGS.Dashboard_InfoNassauSimpleAnnuity;
      visuals[MM_PRODUCT_VISUALS.importantDisclosures] = nfgSAImportantDisclosures;
      break;
    }
    case MM_PRODUCT_ID.nfgMA0500: {
      visuals[MM_PRODUCT_VISUALS.cardColor] = "#597c6f";
      visuals[MM_PRODUCT_VISUALS.cardColorDefault] = "#597c6f";
      visuals[MM_PRODUCT_VISUALS.title] = "Nassau MYAnnuity";
      visuals[MM_PRODUCT_VISUALS.tradeMark] = "®";
      visuals[MM_PRODUCT_VISUALS.info] = MYGA_STRINGS.Dashboard_InfoNassauMYAnnuity;
      visuals[MM_PRODUCT_VISUALS.importantDisclosures] = nfgMAImportantDisclosuresNoFWA;
      break;
    }
    case MM_PRODUCT_ID.nfgMA0510: {
      visuals[MM_PRODUCT_VISUALS.cardColor] = "#597c6f";
      visuals[MM_PRODUCT_VISUALS.cardColorDefault] = "#597c6f";
      visuals[MM_PRODUCT_VISUALS.title] = "Nassau MYAnnuity";
      visuals[MM_PRODUCT_VISUALS.tradeMark] = "®";
      visuals[MM_PRODUCT_VISUALS.info] = MYGA_STRINGS.Dashboard_InfoNassauMYAnnuity;
      visuals[MM_PRODUCT_VISUALS.importantDisclosures] = nfgMAImportantDisclosures;
      break;
    }
    case MM_PRODUCT_ID.nfgMA0700: {
      visuals[MM_PRODUCT_VISUALS.cardColor] = "#ccbb66";
      visuals[MM_PRODUCT_VISUALS.cardColorDefault] = "#ccbb66";
      visuals[MM_PRODUCT_VISUALS.title] = "Nassau MYAnnuity";
      visuals[MM_PRODUCT_VISUALS.tradeMark] = "®";
      visuals[MM_PRODUCT_VISUALS.info] = MYGA_STRINGS.Dashboard_InfoNassauMYAnnuity;
      visuals[MM_PRODUCT_VISUALS.importantDisclosures] = nfgMAImportantDisclosuresNoFWA;
      break;
    }
    case MM_PRODUCT_ID.nfgMA0710: {
      visuals[MM_PRODUCT_VISUALS.cardColor] = "#ccbb66";
      visuals[MM_PRODUCT_VISUALS.cardColorDefault] = "#ccbb66";
      visuals[MM_PRODUCT_VISUALS.title] = "Nassau MYAnnuity";
      visuals[MM_PRODUCT_VISUALS.tradeMark] = "®";
      visuals[MM_PRODUCT_VISUALS.info] = MYGA_STRINGS.Dashboard_InfoNassauMYAnnuity;
      visuals[MM_PRODUCT_VISUALS.importantDisclosures] = nfgMAImportantDisclosures;
      break;
    }
    default:
      // TODO: unsupported product ID
      break;
  }
  
  
  product[MM_PRODUCT.visuals] = visuals;
  return product;
};

export const makePayment = (type, amount=0.0) => {
  const payment = (type === MM_PAYMENT_TYPE.bankEFT) ?
      createObjectForKeys(MM_PAYMENT_EFT, "") :
      createObjectForKeys(MM_PAYMENT, "");
  payment[MM_PAYMENT.paymentID] = uuidv4();
  payment[MM_PAYMENT.paymentType] = type;
  payment[MM_PAYMENT.amount] = amount;
  payment[`${type}_Amount`] = amount;
  // handle EFT case
  if (type===MM_PAYMENT_TYPE.bankEFT) {
    // default to checking
    payment[MM_PAYMENT_EFT.bankAccountType] = MM_PAYMENT_EFT_ACCOUNT_TYPE.checking;
    // set the default payor
    const owner = mygaSession.getCurrentAppOwner();
    if (isValid(owner)) {
      payment[MM_PAYMENT_EFT.payorUid] = owner[PARTY.uid];
    }
  }
  return payment;
}

export const getPageFlowFromProductType = (productType) => {
  const getCurrentProductType = () => {
    let pt = mygaSession.getMygaSessionValue(STORAGE_KEYS.CurrentProductType, MM_PRODUCT_TYPE.myga);
    if (pt === MM_PRODUCT_TYPE.simpleAnnuity) {
      pt = MM_PRODUCT_TYPE.myga;
      mygaSession.setMygaSessionValue(STORAGE_KEYS.CurrentProductType, pt);
    }
    return pt;
  }
  const pt = isValid(productType) ? productType : getCurrentProductType();
  if (pt === MM_PRODUCT_TYPE.myga) return PAGE_FLOW;
  throw new Error(`product type of ${productType} not found.`);
}

/**
 * Compare the current rate with the product DTO rate
 * @param dto
 * @returns {number} 0 if same -1 if rate decreased, 1 if rate increase
 */
export const validateRateForProductDTO = (dto) => {
  const productRate = dto[MM_PRODUCT_DTO.rate];
  const currentRate = getCurrentRateForProductID(dto[MM_PRODUCT_DTO.productID]);
  const diff = Math.round( 1000.0 * (currentRate - productRate)) / 1000.0;
  return diff < 0 ? -1 : diff > 0 ? 1 : 0;
}